// Teppichgrößen-Berater: reine Rechenlogik, keine UI, keine Seiteneffekte.
// Faustregeln verifiziert gegen Kibek, benuta, IKEA, teppich.de (Stand 2026-06).
// Quellenlage je Regel in den Konstanten dokumentiert.

// Handelsübliche Standardgrößen, gespeichert als [kurz, lang] in cm.
export const STANDARD_RECT = [
  [60, 120],
  [80, 150],
  [120, 170],
  [140, 200],
  [160, 230],
  [170, 240],
  [200, 200],
  [200, 290],
  [240, 340],
  [300, 400],
];

export const STANDARD_ROUND = [120, 160, 200, 250]; // Durchmesser cm

// --- Faustregel-Konstanten (cm) ---
const SIDE_DINING = 65; // Esstisch: 60-80 je Seite (IKEA/Kibek/teppich.de)
const SIDE_SOFA_FRONT = 30; // Sofa "vordere Füße": 20-30 je Seite ideal (Kibek)
const DEPTH_SOFA_FRONT = 160; // Teppichtiefe, damit vordere Sofafüße sicher draufstehen
const SIDE_SOFA_FULL = 55; // Sofa komplett drauf: 50-60 Rahmen (Kibek/benuta)
const DEPTH_SOFA_FULL = 240; // deckt Sofatiefe + Couchtisch
const ROOM_WALL_GAP = 40; // Wohnzimmer zentral: ~30-45 Designregel (kein Händlerbeleg)
const BED_SIDE = 50; // Bett: 40-60 Überstand seitlich/Fußende (Kibek/teppich.de)
const BED_RUNNER_W = 70; // Läufer-Variante: 60-80 breit

function area([s, l]) {
  return s * l;
}

// Kleinste Standardgröße, die [lang, kurz] vollständig abdeckt (beide Maße >=).
export function snapUp(lang, kurz) {
  const need = [Math.max(lang, kurz), Math.min(lang, kurz)];
  const fits = STANDARD_RECT.filter(([s, l]) => l >= need[0] && s >= need[1]);
  if (fits.length === 0) return [...STANDARD_RECT].sort((a, b) => area(b) - area(a))[0];
  return fits.sort((a, b) => area(a) - area(b))[0];
}

// Größte Standardgröße, die in [lang, kurz] hineinpasst (beide Maße <=).
export function snapDown(lang, kurz) {
  const max = [Math.max(lang, kurz), Math.min(lang, kurz)];
  const fits = STANDARD_RECT.filter(([s, l]) => l <= max[0] && s <= max[1]);
  if (fits.length === 0) return [...STANDARD_RECT].sort((a, b) => area(a) - area(b))[0];
  return fits.sort((a, b) => area(b) - area(a))[0];
}

function rectResult(lang, kurz, snap, hinweis) {
  const [s, l] = snap(lang, kurz);
  return {
    mode: "rect",
    ziel: { lang: Math.round(Math.max(lang, kurz)), kurz: Math.round(Math.min(lang, kurz)) },
    groesse: { kurz: s, lang: l },
    label: `${s} × ${l} cm`,
    hinweis,
  };
}

// inputs in cm. Gibt Empfehlung + nächste Standardgröße zurück.
export function empfehlung(fall, inputs = {}) {
  switch (fall) {
    case "esstisch": {
      const { laenge, breite } = inputs;
      const lang = Math.max(laenge, breite) + 2 * SIDE_DINING;
      const kurz = Math.min(laenge, breite) + 2 * SIDE_DINING;
      return rectResult(
        lang,
        kurz,
        snapUp,
        `${SIDE_DINING} cm Überstand pro Seite, damit zurückgeschobene Stühle auf dem Teppich bleiben.`
      );
    }
    case "sofa": {
      const { breite, variante = "vorderfuesse" } = inputs;
      if (variante === "komplett") {
        return rectResult(
          breite + 2 * SIDE_SOFA_FULL,
          DEPTH_SOFA_FULL,
          snapUp,
          `Sofa und Couchtisch stehen komplett auf dem Teppich, rundum ca. ${SIDE_SOFA_FULL} cm Rahmen.`
        );
      }
      return rectResult(
        breite + 2 * SIDE_SOFA_FRONT,
        DEPTH_SOFA_FRONT,
        snapUp,
        `Nur die vorderen Sofafüße stehen auf dem Teppich. Mindestens so breit wie das Sofa, ideal ${SIDE_SOFA_FRONT} cm je Seite mehr.`
      );
    }
    case "wohnzimmer": {
      const { laenge, breite } = inputs;
      const lang = Math.max(laenge, breite) - 2 * ROOM_WALL_GAP;
      const kurz = Math.min(laenge, breite) - 2 * ROOM_WALL_GAP;
      return rectResult(
        lang,
        kurz,
        snapDown,
        `Rundum ca. ${ROOM_WALL_GAP} cm Boden zur Wand frei lassen, nie bündig bis zur Wand.`
      );
    }
    case "bett": {
      const { breite, laenge, variante = "quer" } = inputs;
      if (variante === "laeufer") {
        return {
          mode: "laeufer",
          laeufer: { breite: BED_RUNNER_W, laenge: Math.round(laenge) },
          anzahl: 2,
          label: `2 × Läufer ca. ${BED_RUNNER_W} × ${Math.round(laenge)} cm`,
          hinweis: `Zwei Läufer links und rechts vom Bett, je ca. ${BED_RUNNER_W} cm breit.`,
        };
      }
      // Quer: Teppich unter dem unteren Bettteil, ragt seitlich und am Fußende heraus.
      const lang = breite + 2 * BED_SIDE;
      const kurz = Math.round(laenge / 3) + BED_SIDE;
      return rectResult(
        lang,
        kurz,
        snapUp,
        `Teppich liegt unter dem unteren Drittel des Bettes und ragt seitlich und am Fußende je ca. ${BED_SIDE} cm heraus.`
      );
    }
    default:
      throw new Error(`Unbekannter Fall: ${fall}`);
  }
}

export const FAELLE = ["esstisch", "sofa", "wohnzimmer", "bett"];
